import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useOffline } from '@/context/OfflineContext';
import { cn } from '@/lib/utils';
import { FaWifi, FaSyncAlt, FaCheckCircle } from 'react-icons/fa';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface OfflineIndicatorProps {
  className?: string;
  showSyncStatus?: boolean;
}

export function OfflineIndicator({
  className,
  showSyncStatus = true,
}: OfflineIndicatorProps) {
  const { t } = useTranslation();
  const { isOffline, syncStatus, syncNow } = useOffline();

  const handleSyncClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    syncNow();
  };

  const formatLastSync = () => {
    if (!syncStatus.lastSync) return t('sync.never');

    const now = new Date();
    const lastSync = new Date(syncStatus.lastSync);
    const diffMs = now.getTime() - lastSync.getTime();

    if (diffMs < 60000) {
      return t('sync.justNow');
    }

    if (diffMs < 3600000) {
      const minutes = Math.floor(diffMs / 60000);
      return t('sync.minutesAgo', { count: minutes });
    }

    if (diffMs < 86400000) {
      const hours = Math.floor(diffMs / 3600000);
      return t('sync.hoursAgo', { count: hours });
    }

    return lastSync.toLocaleDateString('es-AR');
  };

  const pendingLabel =
    syncStatus.pending === 1
      ? t('sync.pending', { count: syncStatus.pending })
      : t('sync.pending_plural', { count: syncStatus.pending });

  return (
    <div
      className={cn("flex items-center gap-2 text-xs", className)}
      role="status"
      aria-live="polite"
    >
      {isOffline ? (
        <FaWifi className="h-4 w-4 text-red-500 animate-pulse" title={t('common.offline')} />
      ) : (
        <FaWifi className="h-4 w-4 text-green-500" title={t('common.online')} />
      )}

      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center gap-1.5">
              <span className="hidden sm:inline">
                {isOffline ? t('common.offline') : t('common.online')}
              </span>
            </div>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            <p>
              {isOffline ? t('sync.offlineTooltip') : t('sync.onlineTooltip')}
            </p>
            {showSyncStatus && syncStatus.pending > 0 && (
              <p className="mt-1 text-xs">
                {pendingLabel}
              </p>
            )}
            {showSyncStatus && syncStatus.lastSync && (
              <p className="mt-1 text-xs">{t('sync.lastSynced', { time: formatLastSync() })}</p>
            )}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {showSyncStatus && syncStatus.pending > 0 && (
        <div className="flex items-center gap-1">
          <span className="text-xs">{pendingLabel}</span>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={handleSyncClick}
                  disabled={isOffline || syncStatus.syncing}
                  aria-label={t('sync.syncNow')}
                >
                  <FaSyncAlt
                    className={cn(
                      'h-3 w-3',
                      syncStatus.syncing && 'animate-spin'
                    )}
                  />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p>
                  {isOffline
                    ? t('sync.cannotSyncOffline')
                    : syncStatus.syncing
                    ? t('sync.syncing')
                    : t('sync.syncNow')}
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      )}

      {showSyncStatus && !isOffline && syncStatus.pending === 0 && syncStatus.lastSync && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <FaCheckCircle className="h-3 w-3 text-primary" />
                <span className="hidden sm:inline">{t('sync.synced')}</span>
              </div>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              <p>{t('sync.allSynced')}</p>
              <p className="mt-1 text-xs">{t('sync.lastSynced', { time: formatLastSync() })}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
}
